import { BaseWebComponentController } from '../../../framework/BaseWebComponentController';
import { Dependencies, WacPagesGridEvents, WacPagesRecord } from '../../webAccessControlCustomPagesApp/types';
import { WebAccessControlCustomPagesFormComponent } from '../component/WebAccessControlCustomPagesFormComponent';
import { WebAccessControlCustomPagesFormRepository } from '../repository/WebAccessControlCustomPagesFormRepository';
import { templateWebAccessControlCustomPagesForm } from '../view/template';
import { WebAccessControlCustomPagesFormView } from '../view/WebAccessControlCustomPagesFormView';
import { GzFormFieldWebComponent } from '../../../ui/gzForm/formField';
import { showErrorToast, showSuccessToast } from '../../../ui/gzToastNotification';
import { translateFn } from '../../../utils/translator.helper';
import { ValidationErrorCode } from '../types';

export class WebAccessControlCustomPagesFormController extends BaseWebComponentController {
  public component: WebAccessControlCustomPagesFormComponent;
  public dataRepository: WebAccessControlCustomPagesFormRepository;
  public view: WebAccessControlCustomPagesFormView;
  public viewConstructor: typeof WebAccessControlCustomPagesFormView;
  public dataRepositoryConstructor: typeof WebAccessControlCustomPagesFormRepository;
  public dependencies: Dependencies;

  constructor(component: WebAccessControlCustomPagesFormComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: WebAccessControlCustomPagesFormView,
      viewTemplate: templateWebAccessControlCustomPagesForm,
      dataRepositoryConstructor: WebAccessControlCustomPagesFormRepository,
    });
  }

  public computeEventListeners() {
    return [
      {
        element: this.view.dom.$btnCancel,
        event: 'click',
        callback: this.closeCustomPagesForm,
      },
      {
        element: this.view.dom.$btnSave,
        event: 'click',
        callback: this.saveCustomPagesForm,
      },
    ];
  }

  async getDataForView(): Promise<{ customPage: WacPagesRecord | undefined }> {
    let customPage: WacPagesRecord | undefined;
    this.view.setLoadingState();

    if (!this.component.customPage && this.component.isVisible) {
      customPage = WebAccessControlCustomPagesFormView.getDefaultCustomPage();
      customPage.name = await this.dataRepository.getSuggestedFieldValue();
    } else {
      customPage = this.component.customPage;
    }

    return {
      customPage: customPage,
    };
  };

  private closeCustomPagesForm() {
    (this.view.dom.$hostElement.querySelector(".content") as HTMLElement).scrollTop = 0;
    this.component.propertiesSet = false;
    this.component.isVisible = false;
    this.component.customPage = WebAccessControlCustomPagesFormView.getDefaultCustomPage();
    this.component.propertiesSet = true;
  }

  private async saveCustomPagesForm() {
    this.view.dom.$btnSave.disabled = true;
    this.view.dom.$btnSave.showSpinner = true;
    const record = this.view.getFormData();

    let error;
    if (!record?._id) {
      error = await this.createCustomPages(record);
    } else {
      error = await this.updateCustomPages(record);
    }

    this.view.dom.$btnSave.disabled = false;
    this.view.dom.$btnSave.showSpinner = false;
    if (error) {
      this.displayErrorMessage(error);
      return;
    }

    const message = (!record?._id) ? translateFn('WAC Custom Page created successfully.') : translateFn('WAC Custom Page updated successfully.');
    showSuccessToast(message);

    this.closeCustomPagesForm();
    this.dispatchEvent(WacPagesGridEvents.SAVE_PAGE);
  }

  private dispatchEvent(event: string, detail = {}) {
    this.component.dispatchEvent(
      new CustomEvent(event, {
        detail: detail,
        bubbles: true,
        composed: true,
      })
    );
  }

  private displayErrorMessage(error: { code: number }): void {
    const customPageName: GzFormFieldWebComponent = this.view.dom.$pageNameFieldForm;
    switch (error.code) {
      case ValidationErrorCode.DUPLICATE:
        customPageName.setError(translateFn("A custom page with the same name already exists."));
        break;
      case ValidationErrorCode.MAX_LENGTH:
        customPageName.setError(translateFn('Maximum length permitted is {maxLength} characters', {maxLength: 128}));
        break;
      case ValidationErrorCode.MIN_LENGTH:
        customPageName.setError(translateFn('The minimum length permitted is {minLength} characters', {minLength: 1}));
        break;
      case ValidationErrorCode.REQUIRED_ALLOW_TO_CHANGE:
        this.view.dom.$btnToggleSwitchAllowChange.setError(translateFn('Web access control custom page must be shared because it belongs to another company.'));
        showErrorToast(translateFn("Web access control custom page must be shared because it belongs to another company."));
        break;
      default:
        showErrorToast(translateFn("An unknown error has occurred."));
        break;
    }
  }

  private async createCustomPages(record: WacPagesRecord): Promise<void | string> {
    let error;
    try {
      await this.dataRepository.createRecords([record]);
    } catch (e) {
      error = e?.err || "unknownError";
    }
    return error
  }

  private async updateCustomPages(record: WacPagesRecord): Promise<void | string> {
    let error;
    try {
      await this.dataRepository.updateRecords([record]);
    } catch (e) {
      error = e?.err || "unknownError";
    }
    return error;
  }

}
